import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Center,
  Combobox,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
  useCombobox,
  useMantineColorScheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconCrown,
  IconCrownOff,
  IconUserMinus,
  IconUserPlus,
} from "@tabler/icons-react";
import {
  useTeamDetailsQuery,
  useUpdateTeamNameMutation,
  useSetTeamActiveMutation,
  useAddPlayerToTeamMutation,
  useRemovePlayerFromTeamMutation,
  useSetTeamCaptainMutation,
  useUserSearchLazyQuery,
} from "generated/graphql";

/**
 * TeamEdit
 *
 * Admin view for editing a specific team.
 * Includes roster management: add/remove players, set captain.
 */
export default function TeamEdit() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Fetch team details
  // errorPolicy: "all" allows partial data when some fields fail auth (e.g., mmr requires auth)
  const { data, loading, error, refetch } = useTeamDetailsQuery({
    variables: { teamId: teamId! },
    skip: !teamId,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  // Mutations
  const [updateTeamName, { loading: saving }] = useUpdateTeamNameMutation();
  const [setTeamActive, { loading: savingActive }] = useSetTeamActiveMutation();
  const [addPlayerToTeam, { loading: addingPlayer }] = useAddPlayerToTeamMutation();
  const [removePlayerFromTeam, { loading: removingPlayer }] = useRemovePlayerFromTeamMutation();
  const [setTeamCaptain, { loading: settingCaptain }] = useSetTeamCaptainMutation();

  // User search for adding players
  const [searchUsers, { data: searchData, loading: searching }] = useUserSearchLazyQuery();

  // Local editable state
  const [name, setName] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Player search state
  const [searchValue, setSearchValue] = useState("");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Populate form when data loads
  useEffect(() => {
    if (data?.team) {
      setName(data.team.name);
      setActive(data.team.active);
      setHasChanges(false);
    }
  }, [data]);

  // Track changes
  useEffect(() => {
    if (data?.team) {
      setHasChanges(name !== data.team.name || active !== data.team.active);
    }
  }, [name, active, data]);

  // Search users when typing
  useEffect(() => {
    if (searchValue.length >= 2) {
      searchUsers({ variables: { input: searchValue } });
    }
  }, [searchValue, searchUsers]);

  async function handleSave() {
    if (!teamId || !name.trim()) return;

    try {
      const nameChanged = name !== data?.team?.name;
      const activeChanged = active !== data?.team?.active;

      if (nameChanged) {
        const result = await updateTeamName({
          variables: { teamId, teamName: name.trim() },
        });
        if (!result.data?.updateTeamName) {
          notifications.show({ title: "Error", message: "Failed to update team name", color: "red" });
          return;
        }
      }

      if (activeChanged) {
        const result = await setTeamActive({
          variables: { teamId, active },
        });
        if (!result.data?.setTeamActive) {
          notifications.show({ title: "Error", message: "Failed to update team status", color: "red" });
          return;
        }
      }

      notifications.show({ title: "Success", message: "Team updated successfully", color: "green" });
      setHasChanges(false);
    } catch (err) {
      notifications.show({ title: "Error", message: "An error occurred while updating the team", color: "red" });
    }
  }

  async function handleAddPlayer(userId: string, userName: string) {
    if (!teamId) return;

    try {
      const result = await addPlayerToTeam({
        variables: { teamId, userId },
      });

      if (result.data?.addPlayerToTeam) {
        notifications.show({
          title: "Player Added",
          message: `${userName} has been added to the team`,
          color: "green",
        });
        setSearchValue("");
        combobox.closeDropdown();
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to add player",
        color: "red",
      });
    }
  }

  async function handleRemovePlayer(userId: string, userName: string) {
    if (!teamId) return;

    try {
      const result = await removePlayerFromTeam({
        variables: { teamId, userId },
      });

      if (result.data?.removePlayerFromTeam) {
        notifications.show({
          title: "Player Removed",
          message: `${userName} has been removed from the team`,
          color: "orange",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to remove player",
        color: "red",
      });
    }
  }

  async function handleSetCaptain(userId: string | null, userName: string) {
    if (!teamId) return;

    try {
      const result = await setTeamCaptain({
        variables: { teamId, userId },
      });

      if (result.data?.setTeamCaptain) {
        notifications.show({
          title: userId ? "Captain Set" : "Captain Removed",
          message: userId ? `${userName} is now the team captain` : "Captain has been removed",
          color: "blue",
        });
        refetch();
      }
    } catch (err: any) {
      notifications.show({
        title: "Error",
        message: err?.message || "Failed to set captain",
        color: "red",
      });
    }
  }

  if (loading && !data) {
    return (
      <Center style={{ height: "calc(100dvh - 48px)" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  // With errorPolicy: "all", we may have partial data even with errors (e.g., mmr auth failure)
  // Only show error state if there's no team data at all
  if (!data?.team) {
    return (
      <Container size="xl" px="lg" py="lg">
        <Group justify="flex-start" mb="sm">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin-hub/teams")}
          >
            Back to Teams
          </Button>
        </Group>
        <Text c="red">Failed to load team data.</Text>
      </Container>
    );
  }

  const team = data.team;
  const players = team.players || [];
  const currentPlayerIds = new Set(players.map((p) => p.id));

  // Filter search results to exclude players already on the team
  const searchResults = (searchData?.users?.items || []).filter(
    (user) => !currentPlayerIds.has(user.id)
  );

  const isRosterLoading = addingPlayer || removingPlayer || settingCaptain;

  return (
    <Container size="xl" px="lg" py="lg">
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin-hub/teams")}
          size="sm"
        >
          Back to Teams
        </Button>
      </Group>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Team Details */}
        <div className="space-y-4">
          <Title order={2} c={isDark ? "white" : "black"}>
            Edit Team
          </Title>
          <Text size="sm" c={isDark ? "gray.4" : "gray.7"}>
            Team ID: {teamId}
          </Text>

          <div className="space-y-4">
            <TextInput
              label="Team Name"
              placeholder="Enter team name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              withAsterisk
            />

            <TextInput
              label="Franchise"
              value={team.franchise ? `[${team.franchise.prefix ?? ""}] ${team.franchise.name}` : "No franchise"}
              disabled
              description="Franchise assignment cannot be changed after team creation"
            />

            <Group align="center">
              <Switch
                checked={active}
                onChange={(e) => setActive(e.currentTarget.checked)}
                label="Active"
                disabled={savingActive}
              />
            </Group>

            <Group mt="md">
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={handleSave}
                disabled={!hasChanges || !name.trim() || saving || savingActive}
                loading={saving || savingActive}
              >
                Save Changes
              </Button>
              <Button variant="light" color="gray" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </Group>
          </div>
        </div>

        {/* Right Column: Roster Management */}
        <div className="space-y-4">
          <Title order={2} c={isDark ? "white" : "black"}>
            Team Roster
          </Title>

          {/* Add Player Section */}
          <Paper p="md" withBorder>
            <Text size="sm" fw={500} mb="xs">
              Add Player
            </Text>
            <Combobox
              store={combobox}
              onOptionSubmit={(val) => {
                const user = searchResults.find((u) => u.id === val);
                if (user) {
                  handleAddPlayer(user.id, user.name);
                }
              }}
            >
              <Combobox.Target>
                <TextInput
                  placeholder="Search players by name..."
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.currentTarget.value);
                    combobox.openDropdown();
                  }}
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  rightSection={searching ? <Loader size="xs" /> : <IconUserPlus size={16} />}
                  disabled={isRosterLoading}
                />
              </Combobox.Target>

              <Combobox.Dropdown>
                <Combobox.Options>
                  {searchValue.length < 2 ? (
                    <Combobox.Empty>Type at least 2 characters to search</Combobox.Empty>
                  ) : searching ? (
                    <Combobox.Empty>Searching...</Combobox.Empty>
                  ) : searchResults.length === 0 ? (
                    <Combobox.Empty>No players found</Combobox.Empty>
                  ) : (
                    searchResults.map((user) => (
                      <Combobox.Option key={user.id} value={user.id}>
                        {user.name}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          </Paper>

          <Divider />

          {/* Current Roster */}
          <Text size="sm" c="dimmed">
            {players.length} player{players.length !== 1 ? "s" : ""} on roster
          </Text>

          <Stack gap="xs">
            {players.length === 0 ? (
              <Paper p="md" withBorder>
                <Text c="dimmed" ta="center">
                  No players on this team
                </Text>
              </Paper>
            ) : (
              players.map((player) => (
                <Paper key={player.id} p="sm" withBorder>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar src={player.avatarUrl} size="sm" radius="xl">
                        {player.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <div>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {player.name}
                          </Text>
                          {player.isCaptain && (
                            <Badge size="xs" color="yellow" leftSection={<IconCrown size={10} />}>
                              Captain
                            </Badge>
                          )}
                        </Group>
                        {player.mmr && (
                          <Text size="xs" c="dimmed">
                            MMR: {player.mmr}
                          </Text>
                        )}
                      </div>
                    </Group>

                    <Group gap="xs" wrap="nowrap">
                      {player.isCaptain ? (
                        <Tooltip label="Remove as Captain">
                          <ActionIcon
                            variant="light"
                            color="yellow"
                            onClick={() => handleSetCaptain(null, player.name)}
                            disabled={isRosterLoading}
                          >
                            <IconCrownOff size={16} />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Tooltip label="Set as Captain">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={() => handleSetCaptain(player.id, player.name)}
                            disabled={isRosterLoading}
                          >
                            <IconCrown size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Remove from Team">
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => handleRemovePlayer(player.id, player.name)}
                          disabled={isRosterLoading}
                        >
                          <IconUserMinus size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                </Paper>
              ))
            )}
          </Stack>
        </div>
      </div>
    </Container>
  );
}
